import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTenant } from './useTenant';
import { useAuth } from './useAuth';
import { selectTrialInfo } from '@/features/billing/redux/trial-selectors';

interface TrialDaysInfo {
  totalTrialDays: number;
  daysElapsed: number;
  daysRemaining: number;
  trialEndsAt: Date | null;
  isInTrial: boolean;
  progressPercentage: number;
}

const SIN_PRUEBA: TrialDaysInfo = {
  totalTrialDays: 0,
  daysElapsed: 0,
  daysRemaining: 0,
  trialEndsAt: null,
  isInTrial: false,
  progressPercentage: 0,
};

const DIA_MS = 1000 * 60 * 60 * 24;

/**
 * Días de prueba del Header («Te quedan N días de prueba») y del flyout de impersonación.
 *
 * GH#2236 — los días, el fin y si está en prueba salen de `GET /subscriptions/trial-days`
 * (`state.trial`, el mismo que usan la barra lateral, el banner y la tarjeta de Mi
 * suscripción), que el backend calcula con el fin EFECTIVO que cumplen el cron y el guard.
 * Antes este hook hacía su propia cuenta con `trialEndsAt` del tenant (el guardado, alta+14),
 * y si faltaba caía a `expiresAt` —la fecha de RENOVACIÓN de un plan pago—, así que el Header
 * podía decir 14 donde la barra lateral decía 30, o mostrar «días de prueba» a quien ya pagó.
 * Lo único que se calcula acá es la barra de progreso, desde el alta hasta ese mismo fin.
 */
export const useTrialDays = (): TrialDaysInfo => {
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const prueba = useSelector(selectTrialInfo);

  return useMemo(() => {
    if (!currentTenant || !user) return SIN_PRUEBA;
    // Mismo criterio que antes: en prueba con al menos un día por delante.
    if (!prueba.isInTrial || prueba.daysRemaining <= 0 || !prueba.trialEndsAt) return SIN_PRUEBA;

    const trialEndsAt = new Date(prueba.trialEndsAt);
    const now = new Date();
    const trialStartAt = (currentTenant as any).trialStartAt
      ? new Date((currentTenant as any).trialStartAt)
      : new Date(currentTenant.createdAt);

    const daysElapsed = Math.max(0, Math.floor((now.getTime() - trialStartAt.getTime()) / DIA_MS));
    const totalTrialDays = Math.max(0, Math.ceil((trialEndsAt.getTime() - trialStartAt.getTime()) / DIA_MS));
    const progressPercentage =
      totalTrialDays > 0 ? Math.min(100, Math.max(0, (daysElapsed / totalTrialDays) * 100)) : 0;

    return {
      totalTrialDays,
      daysElapsed,
      daysRemaining: prueba.daysRemaining,
      trialEndsAt,
      isInTrial: true,
      progressPercentage,
    };
  }, [currentTenant, user, prueba]);
};
